import { useState, useEffect, useRef } from 'react';
import { SessionStorage } from '../utils/sessionStorage';

/**
 * Hook para gerenciar sessionID
 *
 * @param fixedSessionId - SessionID fixo (tem prioridade sobre generateSessionId)
 * @param generateSessionId - Função opcional para gerar sessionID personalizado
 * @param loadPreviousSession - Se deve carregar sessão anterior do localStorage (padrão: false)
 * @returns sessionId e função para resetar sessão
 */
export const useSessionId = (
  fixedSessionId?: string,
  generateSessionId?: () => string,
  loadPreviousSession: boolean = false
) => {
  const [sessionId, setSessionId] = useState<string>('');
  const generateFnRef = useRef(generateSessionId);
  const fixedIdRef = useRef(fixedSessionId);

  // Atualiza refs quando props mudam
  useEffect(() => {
    generateFnRef.current = generateSessionId;
  }, [generateSessionId]);

  useEffect(() => {
    fixedIdRef.current = fixedSessionId;
  }, [fixedSessionId]);

  useEffect(() => {
    // Se tem sessionID fixo, usa ele diretamente
    if (fixedIdRef.current) {
      setSessionId(fixedIdRef.current);
      return;
    }

    // Se loadPreviousSession for false, sempre cria uma nova sessão
    if (!loadPreviousSession) {
      const newId = SessionStorage.createNewSessionId(generateFnRef.current);
      setSessionId(newId);
      return;
    }

    // Se loadPreviousSession for true, obtém ou cria sessionID via localStorage
    const id = SessionStorage.getOrCreateSessionId(generateFnRef.current);
    setSessionId(id);
  }, [loadPreviousSession]);

  /**
   * Reseta a sessão criando um novo ID
   * Nota: Se estiver usando sessionID fixo, resetSession não terá efeito
   */
  const resetSession = () => {
    if (fixedIdRef.current) {
      console.warn('[useSessionId] resetSession não tem efeito quando sessionId fixo é fornecido');
      return fixedIdRef.current;
    }

    const newId = SessionStorage.resetSession(generateFnRef.current);
    setSessionId(newId);
    return newId;
  };

  /**
   * Limpa a sessão
   * Nota: Se estiver usando sessionID fixo, clearSession não terá efeito
   */
  const clearSession = () => {
    if (fixedIdRef.current) {
      console.warn('[useSessionId] clearSession não tem efeito quando sessionId fixo é fornecido');
      return;
    }

    SessionStorage.clearSession();
    setSessionId('');
  };

  return {
    sessionId,
    resetSession,
    clearSession,
  };
};
